import React, { useMemo } from "react";
import { Avatar, Button, Empty, Tabs, Tag } from "antd";
import { ArrowRightOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import AttachmentViewer from "@/components/AttachmentViewer";
import { CONTRACT_STATUS_COLORS, CONTRACT_STATUS_LABELS } from "./constants";
import {
  ActivityHeaderAction,
  EmptyLogs,
  LogAction,
  LogActor,
  LogDot,
  LogItem,
  LogList,
  LogMessage,
  LogMeta,
  LogTime,
  NoteHint,
  StatusTransition,
  TransitionArrow,
} from "./ContractActivity.styles";
import {
  AttachmentDate,
  AttachmentList,
  AttachmentMain,
  AttachmentRow,
  AttachmentSide,
  EmptyNotes,
  QuoteMeta,
  Timeline,
  TimelineAuthor,
  TimelineItem,
  TimelineMeta,
  TimelineText,
  TimelineTime,
} from "./ContractDetail.styles";

const TERMINAL_STATUSES = new Set(["closed", "cancelled"]);

// The two note channels rendered on the contract-request detail page. Kept in
// sync with ContractNote.CHANNEL_* on the backend, which decides the channel
// server side from the author (never from the client).
export const NOTE_CHANNEL_ANNOUNCEMENT = "announcement";
export const NOTE_CHANNEL_SUBMISSION = "submission";

/**
 * Resolve the channel of a note. The backend always sends `channel`; the author
 * check is only a fallback for payloads cached before the field existed.
 */
export const resolveNoteChannel = (note) => {
  if (
    note?.channel === NOTE_CHANNEL_ANNOUNCEMENT ||
    note?.channel === NOTE_CHANNEL_SUBMISSION
  ) {
    return note.channel;
  }
  return note?.createdby_contract_company
    ? NOTE_CHANNEL_SUBMISSION
    : NOTE_CHANNEL_ANNOUNCEMENT;
};

export const splitNotesByChannel = (notes) => {
  const announcements = [];
  const submissions = [];
  (notes || []).forEach((note) => {
    if (resolveNoteChannel(note) === NOTE_CHANNEL_SUBMISSION) {
      submissions.push(note);
    } else {
      announcements.push(note);
    }
  });
  return { announcements, submissions };
};

const ACTOR_TYPE_LABELS = {
  facility: "Facility user",
  contractor: "Contractor",
  system: "System",
};

const getInitials = (name) => {
  const value = String(name || "?").trim();
  if (!value) return "?";
  const parts = value.split(/\s+/).filter(Boolean);
  return (
    parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : value.slice(0, 2)
  ).toUpperCase();
};

const formatDate = (value) => (value ? moment(value).format("YYYY-MM-DD HH:mm") : "-");

const statusLabel = (status) => CONTRACT_STATUS_LABELS[status] || status || "None";

const describeActor = (log) => {
  const name = log.actor_name || log.actor_company_name || "System";
  const type = ACTOR_TYPE_LABELS[log.actor_type];
  return type ? `${name} - ${type}` : name;
};

/**
 * One notes panel, split into two tabs: the text feed (tab 1, named after the
 * channel - Announcement / Submission) and every attachment of the channel
 * (tab 2). The same component backs both panels - they only differ in the
 * copy, the hint, and the notes they are handed. System events never show up
 * here (they live in the Logs panel).
 *
 * Visibility is filtering done server side: an announcement is public to every
 * party that can see the request, a submission is only ever sent to its own
 * contractor plus the facility side. Delete permissions are also enforced
 * server side; `canDeleteNote` only decides whether the button is rendered.
 */
export function ContractNotesPanel({
  notes,
  canAddNote,
  onAddNote,
  addLabel = "Add Note",
  emptyText = "No notes yet",
  hint = null,
  textTabLabel = "Notes",
  canDeleteNote,
  onDeleteNote,
  onDeleteAttachment,
  directAttachments = [],
}) {
  const sortedNotes = useMemo(
    () =>
      [...(notes || [])].sort(
        (a, b) => new Date(b.created_date) - new Date(a.created_date)
      ),
    [notes]
  );

  // Tab 2 aggregates every attachment of the channel, newest first, keeping a
  // reference to the source note so the delete handler has the context it
  // needs. `directAttachments` are files that were dropped straight into the
  // contract's managed folder in /file-manage (no backing note): they render
  // with note=null (so no delete button - deletion happens in /file-manage)
  // and look exactly like any other attachment.
  const attachmentItems = useMemo(() => {
    const items = [];
    sortedNotes.forEach((note) => {
      (note.attachments || []).forEach((attachment) => {
        items.push({ note, attachment });
      });
    });
    (directAttachments || []).forEach((attachment) => {
      items.push({ note: null, attachment });
    });
    return items.sort(
      (a, b) =>
        new Date(b.attachment.created_date || 0) -
        new Date(a.attachment.created_date || 0)
    );
  }, [sortedNotes, directAttachments]);

  // No Tooltip here on purpose: the trash icon next to a note / attachment row
  // is self-explanatory, and the confirm modal already spells out what is
  // about to be deleted. `aria-label` keeps the action name for screen readers
  // without showing a hover bubble.
  const renderDeleteButton = (label, onClick) => (
    <Button
      type="text"
      size="small"
      danger
      icon={<DeleteOutlined />}
      aria-label={label}
      onClick={onClick}
    />
  );

  const textTab = sortedNotes.length ? (
    <Timeline>
      {sortedNotes.map((item) => {
        const author = item.createdby_name || "Unknown";
        const authorEmail = item.createdby_contract_company_email;
        return (
          <TimelineItem key={item.id || `${item.created_date}-${author}`}>
            <Avatar size={36} src={item.createdby_contract_company_logo}>
              {getInitials(author)}
            </Avatar>
            <div>
              <TimelineMeta>
                <TimelineAuthor>{author}</TimelineAuthor>
                <TimelineTime>{formatDate(item.created_date)}</TimelineTime>
                {canDeleteNote?.(item) && onDeleteNote
                  ? renderDeleteButton(`Delete this ${textTabLabel.toLowerCase()}`, () => onDeleteNote(item))
                  : null}
              </TimelineMeta>
              {authorEmail ? <QuoteMeta>{authorEmail}</QuoteMeta> : null}
              <TimelineText
                dangerouslySetInnerHTML={{ __html: item.content || item.text || "" }}
              />
            </div>
          </TimelineItem>
        );
      })}
    </Timeline>
  ) : (
    <EmptyNotes image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />
  );

  const attachmentsTab = attachmentItems.length ? (
    <AttachmentList>
      {attachmentItems.map(({ note, attachment }) => (
        <AttachmentRow key={`${note ? `note-${note.id}` : "direct"}-${attachment.id}`}>
          <AttachmentMain>
            <AttachmentViewer attachment={attachment} />
          </AttachmentMain>
          <AttachmentSide>
            <AttachmentDate>{formatDate(attachment.created_date)}</AttachmentDate>
            {note && canDeleteNote?.(note) && onDeleteAttachment
              ? renderDeleteButton("Delete this attachment", () => onDeleteAttachment(note, attachment))
              : null}
          </AttachmentSide>
        </AttachmentRow>
      ))}
    </AttachmentList>
  ) : (
    <EmptyNotes image={Empty.PRESENTED_IMAGE_SIMPLE} description="No attachments yet" />
  );

  return (
    <>
      {hint ? <NoteHint>{hint}</NoteHint> : null}
      {canAddNote ? (
        <ActivityHeaderAction>
          <Button size="small" icon={<PlusOutlined />} onClick={onAddNote}>
            {addLabel}
          </Button>
        </ActivityHeaderAction>
      ) : null}
      <Tabs size="small">
        <Tabs.TabPane tab={textTabLabel} key="text">
          {textTab}
        </Tabs.TabPane>
        <Tabs.TabPane tab={`Attachment (${attachmentItems.length})`} key="attachments">
          {attachmentsTab}
        </Tabs.TabPane>
      </Tabs>
    </>
  );
}

/**
 * Logs panel: the contract request lifecycle trail (status transitions and
 * related events). Facility only - the backend never serializes logs for a
 * contractor account, and the detail page only renders this panel when
 * showLogs is true (facility / portfolio roles).
 */
export function ContractLogsPanel({ logs }) {
  const sortedLogs = useMemo(
    () =>
      [...(logs || [])].sort(
        (a, b) => new Date(b.created_date) - new Date(a.created_date)
      ),
    [logs]
  );

  return sortedLogs.length ? (
    <LogList>
      {sortedLogs.map((log) => (
        <LogItem key={log.id}>
          <LogDot
            $tone={
              log.to_status && TERMINAL_STATUSES.has(log.to_status)
                ? "terminal"
                : "default"
            }
          />
          <div>
            <LogMeta>
              <LogAction>{log.action_label || log.action}</LogAction>
              <LogTime>{formatDate(log.created_date)}</LogTime>
            </LogMeta>
            {log.from_status || log.to_status ? (
              <StatusTransition>
                <Tag color={CONTRACT_STATUS_COLORS[log.from_status]}>
                  {statusLabel(log.from_status)}
                </Tag>
                <TransitionArrow>
                  <ArrowRightOutlined />
                </TransitionArrow>
                <Tag color={CONTRACT_STATUS_COLORS[log.to_status]}>
                  {statusLabel(log.to_status)}
                </Tag>
              </StatusTransition>
            ) : null}
            {log.message ? <LogMessage>{log.message}</LogMessage> : null}
            <LogActor>{describeActor(log)}</LogActor>
          </div>
        </LogItem>
      ))}
    </LogList>
  ) : (
    <EmptyLogs
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description="No activity logged yet"
    />
  );
}
